"use client";

import React from "react";
import type { AssessmentResult } from "@/types";

type Props = {
  assessmentResults: AssessmentResult | null;
};

export function AssessmentInsightsSidePanel({ assessmentResults }: Props) {
  if (!assessmentResults) {
    return (
      <aside className="hidden w-[min(100%,22rem)] shrink-0 flex-col border-l border-[var(--border-subtle)] bg-[var(--bg-elevated)] shadow-[var(--shadow-card)] lg:flex xl:w-96">
        <div className="flex flex-1 flex-col items-center justify-center p-8 text-center">
          <div className="mb-4 rounded-full bg-[var(--bg-muted)] p-6">
            <svg className="h-10 w-10 text-[var(--text-tertiary)] opacity-20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h3 className="text-sm font-bold text-[var(--text-primary)]">Discovery in Progress</h3>
          <p className="mt-2 text-xs leading-relaxed text-[var(--text-tertiary)] max-w-[200px]">
            Insights will generate upon completion of the assessment workflow.
          </p>
        </div>
      </aside>
    );
  }

  const {
    estimatedSavingsRange = { min: 0, max: 0 },
    confidenceLevel = { score: 0, label: 'Low' },
    findings = [],
    assumptions = [],
    constraints = [],
  } = assessmentResults || {};

  const totalFindingsSavings = findings.reduce((sum, f) => sum + (Number(f.estimatedAmount) || 0), 0);
  const displayMin = (estimatedSavingsRange?.min && estimatedSavingsRange.min > 0) 
    ? estimatedSavingsRange.min 
    : (totalFindingsSavings > 0 ? Math.round(totalFindingsSavings * 0.75) : 0);
  const displayMax = (estimatedSavingsRange?.max && estimatedSavingsRange.max > 0) 
    ? estimatedSavingsRange.max 
    : totalFindingsSavings;

  return (
    <aside className="hidden w-[min(100%,22rem)] shrink-0 flex-col border-l border-[var(--border-subtle)] bg-[var(--bg-elevated)] shadow-[var(--shadow-card)] lg:flex xl:w-96 overflow-hidden">
      {/* Header */}
      <div className="border-b border-[var(--border-subtle)] px-6 py-5">
        <p className="text-[11px] font-bold uppercase tracking-widest text-[var(--brand-primary)]">Analysis Results</p>
        <h2 className="mt-1 text-lg font-bold text-[var(--text-primary)]">FinOps Executive Summary</h2>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Savings Section */}
        <div className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-muted)] p-5 shadow-sm ring-1 ring-[color-mix(in_srgb,var(--brand-primary)_5%,transparent)]">
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-tertiary)]">Estimated Monthly Savings Potential</h3>
          <div className="mt-3">
            <p className="text-3xl font-extrabold tracking-tight text-[var(--brand-primary)]">
              ${displayMin.toLocaleString()} - ${displayMax.toLocaleString()}
            </p>
            <p className="mt-1 text-[11px] text-[var(--text-tertiary)]">Estimated range based on verified workload data</p>
          </div>
          
          <div className="mt-6 space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--text-tertiary)] text-center">Optimization Findings</p>
            <div className="space-y-2">
              {findings.slice(0, 5).map((f, i) => (
                <div key={i} className="flex flex-col gap-1 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-elevated)] px-3 py-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-[var(--text-primary)] truncate max-w-[180px]">{f.category}</span>
                    <span className="text-[10px] font-black text-[var(--brand-primary)]">${f.estimatedAmount.toLocaleString()}</span>
                  </div>
                  <span className="text-[10px] text-[var(--text-tertiary)] line-clamp-2">{f.description}</span>
                </div>
              ))}
            </div>
            {findings.length > 5 && (
              <p className="text-center text-[9px] text-[var(--text-tertiary)] font-medium">+ {findings.length - 5} more findings in full report</p>
            )}
          </div>
        </div>

        {/* Confidence Section */}
        <div className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-elevated)] p-5 shadow-sm">
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-tertiary)]">Confidence Score</h3>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-[var(--text-primary)]">{confidenceLevel.score}%</span>
            <span className="text-xs font-semibold text-[var(--brand-primary)] capitalize">{confidenceLevel.label}</span>
          </div>
          <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-[var(--bg-muted)]">
            <div 
              className="h-full rounded-full bg-brand-gradient transition-all duration-1000" 
              style={{ width: `${confidenceLevel.score}%` }} 
            />
          </div>
        </div>

        {/* Assumptions & Constraints */}
        <div className="space-y-6 pb-4">
          {constraints.length > 0 && (
            <div>
              <h3 className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-tertiary)]">Operational Constraints</h3>
              <ul className="mt-3 space-y-1.5">
                {constraints.map((c, i) => (
                  <li key={i} className="flex gap-2 text-[10px] leading-relaxed text-[var(--text-secondary)]">
                    <svg className="h-3 w-3 text-amber-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          <div>
            <h3 className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-tertiary)]">Key Assumptions</h3>
            <ul className="mt-3 space-y-1.5">
              {assumptions.map((a, i) => (
                <li key={i} className="flex gap-2 text-[10px] leading-relaxed text-[var(--text-secondary)]">
                  <span className="text-[var(--text-tertiary)]">·</span>
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Footer Branding */}
      <div className="border-t border-[var(--border-subtle)] px-6 py-4 bg-[var(--bg-muted)]">
        <p className="text-[9px] text-center font-medium uppercase tracking-[0.2em] text-[var(--text-tertiary)]">
          FinOps Analysis Engine v2.0
        </p>
      </div>
    </aside>
  );
}
